'use client';

import React from 'react';
import Link from 'next/link';
import { useWindowInfo } from '@faceless-ui/window-info';
import dayjs from 'dayjs';
import type { ArticlesQuery } from '../../graphql/generated/schema';
import { AppImage } from '../AppImage';
import styles from './styles.module.scss';
import { truncateString } from '../../utils/truncateString';
import breakpoints from '../../styles/queries/index.module.scss';
import { constructClassName } from '../../utils/constructClassName';
import { Tag } from '../Tag';
import colors from '../../styles/colors/index.module.scss';

interface Props {
  article: NonNullable<Extract<ArticlesQuery['Articles'], { __typename?: 'Articles' }>['docs']>[0];
  largeCard?: boolean;
  priority?: boolean;
}

export const ArticleCard: React.FC<Props> = ({
  article,
  largeCard,
  priority,
}) => {
  const windowInfo = useWindowInfo();
  const isMobile = windowInfo.width && windowInfo.width < parseInt(breakpoints.sm, 10);

  return (
    <Link
      className={styles.linkWrapper}
      href={`/articles/${article?.categories?.[0]?.slug}/${article?.slug}`}
    >
      <div className={styles.container}>
        <AppImage
          className={constructClassName([
            styles.articleFeaturedImage,
            largeCard && styles.largeImage,
          ])}
          imageSize="card"
          image={article?.image}
          priority={priority}
        />
        {
          largeCard ? (
            <div className={styles.tagsContainer}>
              {
                article?.tags?.map((tag, index) => (
                  <Tag
                    key={tag.id}
                    color={index > 0 ? colors.primary : colors.secondary}
                    lighten={index > 0 ? 3 : 0.5}
                  >
                    {tag.title}
                  </Tag>
                ))
              }
            </div>
          ) : null
        }
        <h3 className={constructClassName([
          styles.title,
          largeCard && styles.largeTitle,
        ])}
        >
          {article?.title}
        </h3>
        {
          largeCard ? (
            <span className={styles.publishedDate}>
              {dayjs(article?.publishedAt).format('DD MMM YYYY')}
            </span>
          ) : null
        }
        {
          ((largeCard || isMobile) && article?.excerpt) ? (
            <p className={styles.description}>{truncateString(article?.excerpt, 100)}</p>
          ) : null
        }
        <span className={styles.spacer} />
        <span
          className={styles.link}
        >
          Read in
          {' '}
          {article?.timeToRead}
          {' '}
          Minutes
        </span>
      </div>
    </Link>
  );
};
